import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { ProjectCard } from "@/components/ProjectCard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import API from "@/lib/api";
import { FolderOpen, Zap, Star, FileText, Library, Loader2 } from "lucide-react";
import { StatBar } from "@/components/shared/StatBar";

const EXAMPLE_CONCEPTS = [
  "Invoice management tool for freelancers with payment tracking",
  "Kanban project board with real-time collaboration",
  "AI-powered customer support chatbot with knowledge base",
  "Subscription billing dashboard with usage analytics",
  "Job application tracker with resume parsing",
];

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [recentPrompts, setRecentPrompts] = useState([]);
  const [stats, setStats] = useState({ projects: 0, prompts: 0, favorites: 0, library_blocks: 0 });
  const [loading, setLoading] = useState(true);
  const [quickConcept, setQuickConcept] = useState("");
  const [quickGenerating, setQuickGenerating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    try {
      const [projectsRes, promptsRes, statsRes] = await Promise.all([
        API.get("/projects"),
        API.get("/prompts/recent"),
        API.get("/stats"),
      ]);
      setProjects(projectsRes.data);
      setRecentPrompts(promptsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      console.error("Dashboard load error:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickGenerate = async (e) => {
    if (e) e.preventDefault();
    if (!quickConcept.trim()) return;
    setQuickGenerating(true);
    try {
      const projName = quickConcept.trim().substring(0, 50) + (quickConcept.length > 50 ? "..." : "");
      const { data: project } = await API.post("/projects", { name: projName, description: "" });
      navigate(`/projects/${project.id}`, { state: { quickConcept: quickConcept.trim() } });
    } catch (e) {
      console.error("Quick generate error:", e);
    } finally {
      setQuickGenerating(false);
    }
  };

  const isFirstTime = !loading && projects.length === 0;

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]" data-testid="dashboard-page">
      <Header />
      <main className="max-w-[1400px] mx-auto px-6 py-8">

        {/* === FIRST-TIME USER: Focused onboarding === */}
        {isFirstTime ? (
          <div className="max-w-2xl mx-auto pt-8" data-testid="onboarding-state">
            <div className="text-center mb-8">
              <div className="w-12 h-12 bg-[var(--pf-accent)] flex items-center justify-center mx-auto mb-4">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <h1 className="font-heading text-3xl font-bold tracking-tight text-[var(--pf-text)] mb-2">Build your first prompt</h1>
              <p className="text-sm text-[var(--pf-text-secondary)] max-w-md mx-auto">
                Describe any product idea. staqblox will clarify it, score it, and generate 50+ structured, copy-paste-ready prompt blocks.
              </p>
            </div>

            <div className="border border-[var(--pf-border)] bg-[var(--pf-bg)] mb-6">
              <div className="p-6">
                <Textarea
                  value={quickConcept}
                  onChange={(e) => setQuickConcept(e.target.value)}
                  placeholder="Describe your idea in plain English..."
                  data-testid="onboarding-concept-input"
                  className="rounded-none border-[var(--pf-border)] focus:ring-2 focus:ring-[var(--pf-accent)] resize-none font-mono text-sm min-h-[100px]"
                  rows={4}
                />
                <Button
                  onClick={handleQuickGenerate}
                  disabled={quickGenerating || !quickConcept.trim()}
                  data-testid="onboarding-generate-button"
                  className="w-full mt-3 bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white rounded-none h-11 font-medium text-sm"
                >
                  {quickGenerating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating playbook...</> : <><Zap className="w-4 h-4 mr-1.5" />Generate Prompts</>}
                </Button>
              </div>
            </div>

            {/* Example concepts */}
            <div className="mb-8">
              <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-text-muted)] mb-3 text-center">Or try an example</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {EXAMPLE_CONCEPTS.map((ex) => (
                  <button
                    key={ex}
                    onClick={() => setQuickConcept(ex)}
                    data-testid={`example-${ex.substring(0, 15).replace(/\s+/g, "-").toLowerCase()}`}
                    className="text-xs px-3 py-1.5 border border-[var(--pf-border)] text-[var(--pf-text-secondary)] hover:border-[var(--pf-accent)] hover:text-[var(--pf-accent)] transition-colors cursor-pointer"
                  >
                    {ex}
                  </button>
                ))}
              </div>
            </div>

            {/* How it works */}
            <div className="border border-[var(--pf-border)] bg-[var(--pf-surface)] p-6">
              <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-text-muted)] mb-4 text-center">How it works</p>
              <div className="grid grid-cols-4 gap-4">
                {[
                  { num: "1", title: "Describe", desc: "Enter your product idea in plain English" },
                  { num: "2", title: "Clarify", desc: "AI asks targeted questions to strengthen your concept" },
                  { num: "3", title: "Generate", desc: "Get structured prompt blocks across 5 build phases" },
                  { num: "4", title: "Export", desc: "Copy platform-optimized prompts in one click" },
                ].map(({ num, title, desc }) => (
                  <div key={num} className="text-center">
                    <div className="w-7 h-7 bg-[var(--pf-accent)] text-white flex items-center justify-center mx-auto mb-2 text-xs font-bold">{num}</div>
                    <p className="text-xs font-semibold text-[var(--pf-text)] mb-0.5">{title}</p>
                    <p className="text-[10px] text-[var(--pf-text-muted)] leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* === RETURNING USER: Full dashboard === */}

            {/* Compact Stats Bar */}
            <StatBar
              testId="stats-bar"
              stats={[
                { icon: FolderOpen, label: "playbooks", value: stats.projects, testId: "stat-projects" },
                { icon: FileText, label: "prompts", value: stats.prompts, testId: "stat-prompts" },
                { icon: Library, label: "library blocks", value: stats.library_blocks, testId: "stat-library", onClick: () => navigate("/library") },
                { icon: Star, label: "favorites", value: stats.favorites, testId: "stat-favorites", onClick: () => navigate("/library") },
              ]}
            />

            {/* Quick Generate — compact inline */}
            <div className="border border-[var(--pf-border)] p-4 mb-4" data-testid="quick-generate-section">
              <form onSubmit={handleQuickGenerate} className="flex items-center gap-3">
                <Zap className="w-4 h-4 text-[var(--pf-accent)] shrink-0" />
                <input
                  value={quickConcept}
                  onChange={(e) => setQuickConcept(e.target.value)}
                  placeholder="Describe an idea and go — e.g. Invoice tool for freelancers..."
                  data-testid="quick-concept-input"
                  className="flex-1 bg-transparent border-none outline-none text-sm font-mono text-[var(--pf-text)] placeholder:text-[var(--pf-text-muted)]"
                />
                <Button type="submit" disabled={quickGenerating || !quickConcept.trim()} data-testid="quick-generate-button"
                  className="bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white rounded-none h-8 px-4 text-xs font-medium shrink-0">
                  {quickGenerating ? "Creating..." : "Generate"}
                </Button>
              </form>
            </div>

            {/* Playbooks */}
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading text-lg font-bold tracking-tight">Your Playbooks</h2>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="border border-[var(--pf-border)] p-6 space-y-3">
                    <div className="skeleton-line w-8 h-8" />
                    <div className="skeleton-line w-3/4" />
                    <div className="skeleton-line w-1/2" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {projects.map((p) => (
                  <ProjectCard key={p.id} project={p} />
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
